import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { useMe } from "../lib/auth";
import { BuildingPage } from "../features/building/BuildingPage";
import { BuildingOverview } from "../features/building/BuildingOverview";
import { BuildingSettings } from "../features/building/BuildingSettings";
import { DossierPage } from "../features/dossier/DossierPage";
import { MaterialsPage } from "../features/materials/MaterialsPage";
import { DossiersPage } from "../pages/Dossiers";
import { OrdersPage } from "../features/materials/OrdersPage";
import { ContractPage } from "../features/contracts/ContractPage";
import { ContractsPage } from "../features/contracts/ContractsPage";
import { isBuildingSection } from "./navItems";
import { ChooseOrgPage } from "../pages/ChooseOrg";
import { DirectoryPage } from "../pages/Directory";
import { SalesBillingPage } from "../features/platform/SalesBilling";
import { SalesCustomerPage, SalesCustomersPage } from "../features/platform/SalesCustomers";
import { SalesQuotePage, SalesQuotesPage } from "../features/platform/SalesQuotes";
import { CompanyPage } from "../features/directory/CompanyPage";
import { HomePage } from "../pages/Home";
import { LoginPage } from "../pages/Login";
import { LoginLinkPage } from "../pages/LoginLink";
import { MyTasksPage } from "../pages/MyTasks";
import { ObligationsPage } from "../pages/Obligations";
import { PlanningPage } from "../pages/Planning";
import { LibraryPage } from "../pages/Library";
import { MjopPage } from "../pages/Mjop";
import { ConditionPage } from "../pages/Condition";
import { ConditionFieldPage } from "../pages/ConditionField";
import { MetersPage } from "../pages/Meters";
import { MeterRoundPage } from "../pages/MeterRound";
import { PlatformOrgPage, PlatformPage } from "../pages/Platform";
import { ProfilePage } from "../pages/Profile";
import { HelpArticlePage, HelpNewPage, HelpPage, HelpTicketPage } from "../pages/Help";
import { SupportInboxPage } from "../pages/SupportInbox";
import { SupportNotificationsPage } from "../pages/SupportNotifications";
import { HelpcenterAdminPage, HelpcenterArticlePage } from "../pages/HelpcenterAdmin";
import { StatusAdminPage } from "../pages/StatusAdmin";
import { ChangelogAdminPage } from "../pages/ChangelogAdmin";
import { SupportTeamPage } from "../pages/SupportTeam";
import { ScanPage } from "../pages/Scan";
import { QrPage } from "../pages/QrPublic";
import { QuotePublicPage } from "../pages/QuotePublic";
import { SharePage } from "../pages/SharePublic";
import { ScreenPairPage } from "../pages/ScreenPair";
import { ScreenPage } from "../pages/ScreenPublic";
import { IntakePage } from "../pages/Intake";
import { ReportPage } from "../pages/Report";
import { OrganizationPage } from "../pages/Organization";
import { Layout } from "./Layout";
import { Loading } from "./Status";

/** Alleen voor ingelogde gebruikers met een gekozen organisatie. */
function RequireOrg({ children }: { children: ReactNode }) {
  const { data: me, isPending, isError, isFetching } = useMe();
  const loc = useLocation();
  const terug = encodeURIComponent(loc.pathname + loc.search);
  if (isPending) return <Loading page />;
  // Met bewaarde gegevens (offline) gewoon doorgaan.
  if (isError && !me) return <div className="center">De server is niet bereikbaar. Probeer het later opnieuw.</div>;
  if (!me && isFetching) return <Loading page />;
  if (!me) return <Navigate to={`/inloggen?terug=${terug}`} replace />;
  if (!me.org) return <Navigate to={`/organisatie?terug=${terug}`} replace />;
  return <>{children}</>;
}

/** Onderdeel van een gebouw: Overzicht is een eigen pagina, de rest deelt de gebouwpagina (tekening en lijsten). */
function BuildingRoute() {
  const { section } = useParams();
  if (section === "overzicht") return <BuildingOverview />;
  if (section === "gebouw") return <BuildingSettings />;
  if (section === "dossier") return <DossierPage />;
  if (section === "materialen") return <MaterialsPage />;
  if (section === "contracten") return <ContractsPage />;
  if (section && !isBuildingSection(section)) return <Navigate to=".." relative="path" replace />;
  return <BuildingPage />;
}

export function App() {
  return (
    <Routes>
      <Route path="/inloggen" element={<LoginPage />} />
      <Route path="/inloggen/link" element={<LoginLinkPage />} />
      <Route path="/organisatie" element={<ChooseOrgPage />} />
      {/* Ook zonder inloggen: openbare assetkaart en Probleem melden (docs/MELDINGEN.md). */}
      <Route path="/q/:token" element={<QrPage />} />
      <Route path="/d/:token" element={<SharePage />} />
      {/* Gebouwdashboard op een scherm (docs/SCHERMEN.md). */}
      <Route path="/s/:token" element={<ScreenPage />} />
      <Route path="/scherm" element={<ScreenPairPage />} />
      {/* Online akkoord op een offerte van Klantpunt (docs/VERKOOP.md stap 3). */}
      <Route path="/offerte/:token" element={<QuotePublicPage />} />
      <Route
        element={
          <RequireOrg>
            <Layout />
          </RequireOrg>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="gebouwen/:id/:section?" element={<BuildingRoute />} />
        <Route path="melden" element={<ReportPage />} />
        <Route path="taken" element={<MyTasksPage />} />
        <Route path="keuringen" element={<ObligationsPage />} />
        <Route path="planning" element={<PlanningPage />} />
        <Route path="dossiers" element={<DossiersPage />} />
        <Route path="bestellen" element={<OrdersPage />} />
        <Route path="contracten" element={<ContractsPage />} />
        <Route path="contracten/:contractId" element={<ContractPage />} />
        <Route path="mjop" element={<MjopPage />} />
        <Route path="conditie" element={<ConditionPage />} />
        <Route path="conditie/meting/:id" element={<ConditionFieldPage />} />
        <Route path="meterstanden" element={<MetersPage />} />
        <Route path="adresboek" element={<DirectoryPage />} />
        <Route path="adresboek/bedrijf/:companyId" element={<CompanyPage />} />
        <Route path="inbox" element={<IntakePage />} />
        {/* Oude naam van de inbox. */}
        <Route path="meldingen" element={<Navigate to="/inbox" replace />} />
        <Route path="meterstanden/ronde/:id" element={<MeterRoundPage />} />
        <Route path="gebruikers" element={<OrganizationPage />} />
        <Route path="profiel" element={<ProfilePage />} />
        <Route path="scan" element={<ScanPage />} />
        <Route path="platform" element={<PlatformPage />} />
        <Route path="platform/kennisbank" element={<LibraryPage />} />
        <Route path="platform/klanten" element={<SalesCustomersPage />} />
        <Route path="platform/klanten/:id" element={<SalesCustomerPage />} />
        <Route path="platform/offertes" element={<SalesQuotesPage />} />
        <Route path="platform/offertes/:id" element={<SalesQuotePage />} />
        <Route path="platform/facturatie" element={<SalesBillingPage />} />
        <Route path="platform/support" element={<SupportInboxPage />} />
        <Route path="platform/support/meldingen" element={<SupportNotificationsPage />} />
        <Route path="platform/support/team" element={<SupportTeamPage />} />
        <Route path="platform/helpcenter" element={<HelpcenterAdminPage />} />
        <Route path="platform/status" element={<StatusAdminPage />} />
        <Route path="platform/helpcenter/updates" element={<ChangelogAdminPage />} />
        <Route path="platform/helpcenter/artikel/:id" element={<HelpcenterArticlePage />} />
        <Route path="platform/support/:id" element={<SupportInboxPage />} />
        <Route path="hulp" element={<HelpPage />} />
        <Route path="hulp/nieuw" element={<HelpNewPage />} />
        <Route path="hulp/berichten/:id" element={<HelpTicketPage />} />
        <Route path="hulp/artikel/:slug" element={<HelpArticlePage />} />
        <Route path="platform/:id" element={<PlatformOrgPage />} />
        <Route
          path="*"
          element={
            <div className="center">
              <p>Deze pagina bestaat niet.</p>
            </div>
          }
        />
      </Route>
    </Routes>
  );
}
